import { Dialog } from "@headlessui/react";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import Countdown from "react-countdown";
import { sha384 } from "crypto-hash";
import { v4 as uuid } from "uuid";
import config from "config/config";
import { Trans, useTranslation } from "react-i18next";

import { sendEmailNotification } from "service/notificationservice/sendEmailNotification";
import { resetPassword } from "service/userservice/resetPassword";
import { updateUsername } from "service/userservice/updateUsername";
import { getUserProfile } from "service/userservice/getUserProfile";
import FormInputError from "components/General/FormInputError";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import DialogBackdrop from "components/ModalDialogs/DialogBackdrop";

export default function UserSecurityDialog({ dialogState, setDialogState }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const UUID = uuid();
  const [otpError, setotpError] = useState("");
  const [showOtpError, setshowOtpError] = useState(false);
  const [usernameError, setusernameError] = useState("");
  const [showUsernameError, setshowUsernameError] = useState(false);
  const [passwordError, setpasswordError] = useState("");
  const [showPasswordError, setshowPasswordError] = useState(false);
  const [openEyeCurrentPassword, setOpenEyeCurrentPassword] = useState(true);
  const [openEyePassword, setOpenEyePassword] = useState(true);
  const [openEyeConfirmPassword, setOpenEyeConfirmPassword] = useState(true);
  const [currentCounterTime, setCurrentCounterTime] = useState(null);
  const [passcodeExpired, setPasscodeExpired] = useState(false);
  const [currentCounterIndex, setCurrentCounterIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [confirmationPassCode, setConfirmationPassCode] = useState("");
  const [errorText, setErrorText] = useState("");
  const [showErrorText, setShowErrorText] = useState(false);
  const {
    register,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const onOptionChange = (e) => setUpdateItem(e.target.value);
  const [updateItem, setUpdateItem] = useState("password");
  const { user } = useSelector((state) => state.user);
  const { csrUser } = useSelector((s) => s.csrUser);

  useEffect(() => {
    setStep(1);
    setUpdateItem("password");
  }, [dialogState]);

  async function submitStepOne(data) {
    let result = true;

    setLoading(true);
    let loggedInUserProfile = await getUserProfile(
      "",
      "",
      "",
      "",
      "",
      user.userId
    ).catch((error) => {
      console.log(error);
    });
    setLoading(false);

    let hashedCurrentPassword = (await sha384(data.currentPassword))
      .toString()
      .substring(15, 30);
    if (loggedInUserProfile.password === hashedCurrentPassword) {
      if (updateItem === "username" || updateItem === "usernameandpwd") {
        setLoading(true);
        let userProfile = await getUserProfile(data.username).catch((error) => {
          console.log(error);
        });
        if (userProfile) {
          setLoading(false);
          setshowUsernameError(true);
          setusernameError(t("UserSecurityDialog-usernameError"));
          result = false;
        }
      }

      if (updateItem === "password" || updateItem === "usernameandpwd") {
        let hashedPassword = (await sha384(data.newPassword))
          .toString()
          .substring(15, 30);

        if (
          loggedInUserProfile.userCredentialHistory.some(
            (x) => x === hashedPassword
          )
        ) {
          setLoading(false);
          setpasswordError(t("UserSecurityDialog-passwordError"));
          setshowPasswordError(true);
          result = false;
        }
      }

      if (result) {
        let passcode = randomNumberInRange(100000, 999999);
        setConfirmationPassCode(passcode.toString());
        setLoading(true);
        let securitycode = randomNumberInRange(100000, 999999);
        let sec =
          parseInt(securitycode.toString().substring(3, 4)) === 0
            ? 1
            : parseInt(securitycode.toString().substring(3, 4));
        let confirmationcode = passcode * sec;

        let contentDictionary = {};
        contentDictionary.confirmationcode = confirmationcode.toString();
        contentDictionary.username = user.firstName;
        contentDictionary.securitycode = securitycode.toString();

        await sendEmailNotification(user.emailId, 2, contentDictionary)
          .then((response) => {
            setLoading(false);
            setCurrentCounterTime(
              Date.now() + parseInt(config.OTP_EXPIRY_TIME)
            );
            setCurrentCounterIndex(currentCounterIndex + 1);
            setPasscodeExpired(false);
            setStep(2);
          })
          .catch((error) => {
            console.log(error);
          });
      }
    } else {
      setShowErrorText(true);
      setErrorText(t("UserSecurityDialog-errorText"));
    }
  }

  async function submitStepTwo(formData) {
    if (passcodeExpired) {
      setotpError(t("UserSecurityDialog-otpErrorText"));
      setshowOtpError(true);
    } else {
      if (
        formData.verificationCode === confirmationPassCode ||
        (config.ENABLE_DEFAULT_OTP === "true" &&
          formData.verificationCode === config.DEFAULT_OTP)
      ) {
        if (!csrUser?.userId) {
          if (updateItem === "username" || updateItem === "usernameandpwd") {
            setLoading(true);
            await updateUsername(user.userId, formData.username)
              .then((data) => {
                setLoading(false);
                setStep(3);
              })
              .catch((error) => {
                setLoading(false);
                console.log(error);
              });
          }

          if (updateItem === "password" || updateItem === "usernameandpwd") {
            let hashedPassword = (await sha384(formData.newPassword))
              .toString()
              .substring(15, 30);
            setLoading(true);
            await resetPassword(user.userId, hashedPassword)
              .then((data) => {
                setLoading(false);
                setStep(3);
              })
              .catch((error) => {
                setLoading(false);
                console.log(error);
              });
          }
        } else {
          alert(t("UserSecurityDialog-permissionAlertText"));
        }
      } else {
        setotpError(t("UserSecurityDialog-codeVerificationText"));
        setshowOtpError(true);
      }
    }
  }

  const toggleCurrentPasswordEye = () => {
    setOpenEyeCurrentPassword(!openEyeCurrentPassword);
  };
  const togglePasswordEye = () => {
    setOpenEyePassword(!openEyePassword);
  };

  const toggleConfirmPasswordEye = () => {
    setOpenEyeConfirmPassword(!openEyeConfirmPassword);
  };

  const renderer = ({ minutes, seconds, completed }) => {
    if (completed) {
      setPasscodeExpired(true);
      return (
        <Link
          to=""
          className="mb-4 mr-5"
          onClick={async () => {
            let passcode = randomNumberInRange(100000, 999999);
            setConfirmationPassCode(passcode.toString());
            setLoading(true);
            let securitycode = randomNumberInRange(100000, 999999);
            let sec =
              parseInt(securitycode.toString().substring(3, 4)) === 0
                ? 1
                : parseInt(securitycode.toString().substring(3, 4));
            let confirmationcode = passcode * sec;

            let contentDictionary = {};
            contentDictionary.confirmationcode = confirmationcode.toString();
            contentDictionary.username = user.firstName;
            contentDictionary.securitycode = securitycode.toString();

            await sendEmailNotification(
              user.emailId,
              2,
              contentDictionary
            ).catch((error) => {
              console.log(error);
            });
            setLoading(false);
            setCurrentCounterTime(
              Date.now() + parseInt(config.OTP_EXPIRY_TIME)
            );
            setCurrentCounterIndex(currentCounterIndex + 1);
            setPasscodeExpired(false);
          }}
        >
          {t("UserSecurityDialog-countdown-resendLink")}
        </Link>
      );
    } else {
      return (
        <p className="text-sm">
          {t("UserSecurityDialog-codeExpiresText")}{" "}
          {minutes > 9 ? minutes : "0" + minutes}:
          {seconds > 9 ? seconds : "0" + seconds}
        </p>
      );
    }
  };

  function randomNumberInRange(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  return (
    <Dialog
      open={dialogState}
      onClose={() => setDialogState(false)}
      className="ui-dialogs"
    >
      {/* The backdrop, rendered as a fixed sibling to the panel container */}
      <DialogBackdrop />
      {/* Full-screen container to center the panel */}
      <div className="fixed inset-0 flex items-center justify-center   overflow-y-auto">
        <Dialog.Panel
          role="dialog"
          aria-modal="true"
          className="ui-default-dialog"
        >
          <Dialog.Title className="font-body text-xl font-bold text-left">
            {t("UserSecurityDialog-dialogTitle")}
          </Dialog.Title>
          <button
            className="ui-default-dialog-close"
            aria-label={t("UserSecurityDialog-dialogAriaLabel")}
            onClick={() => setDialogState(false)}
          >
            <svg
              className="ui-icon h-3.5 w-3.5"
              focusable="false"
              aria-hidden="true"
              role="presentation"
            >
              <use href="#ui-icon-close"></use>
            </svg>
          </button>
          <Dialog.Description>
            {t("UserSecurityDialog-dialogDescription")}
          </Dialog.Description>
          <hr />
          <EverlakeLoadingOverlay isLoading={loading}>
            {step === 1 && (
              <form onSubmit={handleSubmit(submitStepOne)}>
                <div className="grid grid-cols-1 gap-4">
                  <div className="w-full mx-auto">
                    <fieldset>
                      <legend className="inline-block mr-6 items-center mb-2">
                        {t("UserSecurityDialog-step1-form-legend")}
                      </legend>
                      <div className="inline-block mr-6 mb-3">
                        <label className="inline-flex items-center font-normal">
                          <input
                            type="radio"
                            value="password"
                            checked={updateItem === "password"}
                            onClick={onOptionChange}
                            className="mr-2"
                            {...register("updateType")}
                          />
                          {t("UserSecurityDialog-step1-form-passwordLabel")}
                        </label>
                      </div>
                      <div className="inline-block mr-6">
                        <label className="inline-flex items-center  font-normal">
                          <input
                            type="radio"
                            value="username"
                            checked={updateItem === "username"}
                            onClick={onOptionChange}
                            className="mr-2"
                            {...register("updateType")}
                          />
                          {t("UserSecurityDialog-step1-form-userNameLabel")}
                        </label>
                      </div>
                      <div className="inline-block mr-6">
                        <label className="inline-flex items-center font-normal">
                          <input
                            type="radio"
                            value="usernameandpwd"
                            checked={updateItem === "usernameandpwd"}
                            onClick={onOptionChange}
                            className="mr-2"
                            {...register("updateType")}
                          />
                          {t(
                            "UserSecurityDialog-step1-form-usernamePasswordLabel"
                          )}
                        </label>
                      </div>
                    </fieldset>
                    {/* Username */}
                    {(updateItem === "username" ||
                      updateItem === "usernameandpwd") && (
                      <div>
                        <label
                          id={`${UUID}-username-label`}
                          htmlFor={`${UUID}-username`}
                          className="block mb-1"
                        >
                          {t("UserSecurityDialog-step1-form-userNameLabel")}
                        </label>
                        <input
                          id={`${UUID}-username`}
                          aria-labelledby={`${UUID}-username-label`}
                          aria-required="true"
                          type="text"
                          placeholder={t(
                            "UserSecurityDialog-step1-form-usernameInputPlaceholder"
                          )}
                          {...register("username", {
                            required: t(
                              "UserSecurityDialog-step1-form-usernameInputPlaceholder"
                            ),
                            validate: {
                              minimumCharCount: (v) =>
                                v.length >= 10 ||
                                "Username should be at least 10 characters long",
                              maximumCharCount: (v) =>
                                v.length <= 50 ||
                                "Username should not more than 50 characters",
                              illegalLetter: (v) =>
                                v.replace(/[A-Za-z0-9\.\-\_]/g, "").length <
                                  1 ||
                                "Username only allow Alphanumeric characters and Underscore (_), hyphen (-) and dot (.)",
                            },
                          })}
                          onKeyUp={() => {
                            setusernameError("");
                            setshowUsernameError(false);
                          }}
                          onKeyDown={() => {
                            setusernameError("");
                            setshowUsernameError(false);
                          }}
                        />
                        {errors.username?.message && (
                          <FormInputError
                            errorMessage={errors.username?.message}
                          />
                        )}
                        {showUsernameError && (
                          <FormInputError errorMessage={usernameError} />
                        )}
                        <div
                          id={`${UUID}-new-username-help`}
                          className="mt-1 mb-2"
                        >
                          <div className="text-xs font-semibold text-black">
                            {t(
                              "UserSecurityDialog-step1-form-usernameGuidelines"
                            )}
                          </div>
                          <ul className="list-disc ml-6">
                            <Trans
                              i18nKey="UserSecurityDialog-step1-form-usernameListItems"
                              components={{
                                li: (
                                  <li className="text-xxs font-semibold text-black" />
                                ),
                              }}
                            />
                          </ul>
                        </div>
                      </div>
                    )}

                    {/* current password */}
                    <div className="relative mb-2">
                      <label
                        id={`${UUID}-current-password-label`}
                        htmlFor={`${UUID}-current-password`}
                        className="block mb-1"
                      >
                        {t(
                          "UserSecurityDialog-step1-form-currentPasswordLabel"
                        )}
                      </label>
                      <input
                        id={`${UUID}-current-password`}
                        aria-labelledby={`${UUID}-current-password-label`}
                        aria-required="true"
                        type={openEyeCurrentPassword ? "password" : "text"}
                        autoComplete="off"
                        {...register("currentPassword", {
                          required: t(
                            "UserSecurityDialog-step1-form-currentPasswordRequiredText"
                          ),
                        })}
                        onKeyUp={() => {
                          setErrorText("");
                          setShowErrorText(false);
                        }}
                        onKeyDown={() => {
                          setErrorText("");
                          setShowErrorText(false);
                        }}
                      />
                      <div className="absolute top-8 right-4">
                        {openEyeCurrentPassword ? (
                          <AiOutlineEye
                            onClick={toggleCurrentPasswordEye}
                            className="h-5 w-5 text-ui-blue"
                          ></AiOutlineEye>
                        ) : (
                          <AiOutlineEyeInvisible
                            onClick={toggleCurrentPasswordEye}
                            className="h-5 w-5 text-ui-blue"
                          ></AiOutlineEyeInvisible>
                        )}
                      </div>
                      {errors.currentPassword?.message && (
                        <FormInputError
                          errorMessage={errors.currentPassword?.message}
                        />
                      )}
                      {showErrorText && (
                        <FormInputError errorMessage={errorText} />
                      )}
                    </div>
                    {(updateItem === "password" ||
                      updateItem === "usernameandpwd") && (
                      <>
                        <div className="relative">
                          <label
                            id={`${UUID}-new-password-label`}
                            htmlFor={`${UUID}-new-password`}
                            className="block mb-1"
                          >
                            {t(
                              "UserSecurityDialog-step1-form-newPasswordLabel"
                            )}
                          </label>
                          <div className={"relative"}>
                            <input
                              id={`${UUID}-new-password`}
                              aria-labelledby={`${UUID}-new-password-label`}
                              aria-describedby={`${UUID}-new-password-help`}
                              aria-required="true"
                              type={openEyePassword ? "password" : "text"}
                              autoComplete="off"
                              {...register("newPassword", {
                                required: t(
                                  "UserSecurityDialog-step1-form-newPasswordRequiredText"
                                ),
                                validate: {
                                  minimumCharCount: (v) =>
                                    v.length >= 10 ||
                                    "Password should be at least 10 characters long and less than 61 characters",
                                  maximumCharCount: (v) =>
                                    v.length <= 60 ||
                                    "Password should be at least 10 characters long and less than 61 characters",
                                  hasLowerCaseLetter: (v) =>
                                    /^(?=.*[a-z])/.test(v) ||
                                    "Password should have at least one lowercase letter",
                                  hasCapitalizedCaseLetter: (v) =>
                                    /^(?=.*[A-Z])/.test(v) ||
                                    "Password should have at least one capitalized letter",
                                  hasNumericLetter: (v) =>
                                    /^(?=.*[0-9])/.test(v) ||
                                    "Password should have at least one numeric character",
                                  allowedSpecialChars: (v) =>
                                    /^(?=.*[!@#\$%&\*\(\)])/.test(v) ||
                                    "Password should have at least one special characters: ! @ # $ % & * ( )",
                                  illegalLetter: (v) =>
                                    v.replace(/[A-Za-z0-9!@#\$%&\*\(\)]/g, "")
                                      .length < 1 ||
                                    "MUST USE ONLY ! @ # $ % & * ( ) AS A SPECIAL CHARACTER",
                                },
                              })}
                              onKeyUp={() => {
                                setpasswordError("");
                                setshowPasswordError(false);
                              }}
                              onKeyDown={() => {
                                setpasswordError("");
                                setshowPasswordError(false);
                              }}
                            />
                          </div>
                          <div className="absolute top-8 right-4">
                            {openEyePassword ? (
                              <AiOutlineEye
                                onClick={togglePasswordEye}
                                className="h-5 w-5 text-ui-blue"
                              ></AiOutlineEye>
                            ) : (
                              <AiOutlineEyeInvisible
                                onClick={togglePasswordEye}
                                className="h-5 w-5 text-ui-blue"
                              ></AiOutlineEyeInvisible>
                            )}
                          </div>
                          {errors.newPassword?.message && (
                            <FormInputError
                              errorMessage={errors.newPassword?.message}
                            />
                          )}
                          {showPasswordError && (
                            <FormInputError errorMessage={passwordError} />
                          )}
                          <div
                            id={`${UUID}-new-password-help`}
                            className="mt-1 mb-2"
                          >
                            <div className="text-xs font-semibold text-black">
                              {t(
                                "UserSecurityDialog-step1-form-newPasswordGuidelines"
                              )}
                            </div>
                            <ul className="list-disc ml-6">
                              <Trans
                                i18nKey="UserSecurityDialog-step1-form-newPasswordListItems"
                                components={{
                                  li: (
                                    <li className="text-xxs font-normal text-black" />
                                  ),
                                }}
                              />
                            </ul>
                          </div>
                        </div>
                        {/* confirm password */}
                        <div className="relative mt-4">
                          <label
                            id={`${UUID}-confirm-password-label`}
                            htmlFor={`${UUID}-confirm-password`}
                            className="block mb-1"
                          >
                            {t(
                              "UserSecurityDialog-step1-form-confirmPasswordLabel"
                            )}
                          </label>
                          <div className={"relative"}>
                            <input
                              id={`${UUID}-confirm-password`}
                              aria-labelledby={`${UUID}-confirm-password-label`}
                              aria-describedby={`${UUID}-new-password-help`}
                              aria-required="true"
                              autoComplete="off"
                              type={
                                openEyeConfirmPassword ? "password" : "text"
                              }
                              {...register("confirmedPassword", {
                                required: t(
                                  "UserSecurityDialog-step1-form-confirmPasswordRequiredText"
                                ),
                                validate: (value) => {
                                  if (getValues("newPassword") !== value)
                                    return "Passwords do not match";
                                },
                              })}
                            />
                          </div>
                          <div className="absolute top-8 right-4">
                            {openEyeConfirmPassword ? (
                              <AiOutlineEye
                                onClick={toggleConfirmPasswordEye}
                                className="h-5 w-5 text-ui-blue"
                              ></AiOutlineEye>
                            ) : (
                              <AiOutlineEyeInvisible
                                onClick={toggleConfirmPasswordEye}
                                className="h-5 w-5 text-ui-blue"
                              ></AiOutlineEyeInvisible>
                            )}
                          </div>
                          {errors.confirmedPassword?.message && (
                            <FormInputError
                              errorMessage={errors.confirmedPassword?.message}
                            />
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {/* <!-- Buttons --> */}
                <div className="block text-right mt-6">
                  <button
                    type="button"
                    className="ui-btn-secondary ui-btn-min-w mt-4"
                    onClick={() => setDialogState(false)}
                  >
                    <Trans
                      i18nKey="UserSecurityDialog-step1-form-confirmPasswordCancelButton"
                      components={{
                        span: <span className="sr-only" />,
                      }}
                    />
                  </button>
                  <button className="ui-btn-primary ui-btn-min-w mt-4">
                    {t(
                      "UserSecurityDialog-step1-form-confirmPasswordNextButton"
                    )}
                  </button>
                </div>
              </form>
            )}
            {step === 2 && (
              <form onSubmit={handleSubmit(submitStepTwo)}>
                <div className="w-full mx-auto sm:w-4/5">
                  <div className="text-base font-semibold">
                    {t("UserSecurityDialog-step1-form-confirmIdentityText")}
                  </div>
                  <div className="mx-auto mt-4">
                    <div>
                      {t("UserSecurityDialog-step1-form-accountSecureText")}
                    </div>
                    <div>
                      {" "}
                      {t(
                        "UserSecurityDialog-step1-form-confirmationCodeMessage",
                        {
                          minutes: parseInt(
                            parseInt(config.OTP_EXPIRY_TIME) / 60000
                          ),
                        }
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 mt-4">
                    {/* Your Verification Code */}
                    <div>
                      <label
                        id={`${UUID}-verification-label`}
                        htmlFor={`${UUID}-verification`}
                        className="block mb-1"
                      >
                        {t(
                          "UserSecurityDialog-step1-form-confirmationCodeLabel"
                        )}
                      </label>
                      <input
                        id={`${UUID}-verification`}
                        aria-labelledby={`${UUID}-verification-label`}
                        aria-required="true"
                        type="text"
                        placeholder={t(
                          "UserSecurityDialog-step1-form-confirmationCodeInputPlaceholder"
                        )}
                        {...register("verificationCode", {
                          required: t(
                            "UserSecurityDialog-step1-form-confirmationCodeInputRequiredText"
                          ),
                          pattern: {
                            value: /^\d{6}$/,
                            message: t(
                              "UserSecurityDialog-step1-form-confirmationCodeInputMessage"
                            ),
                          },
                        })}
                        onKeyUp={() => {
                          setotpError("");
                          setshowOtpError(false);
                        }}
                        onKeyDown={() => {
                          setotpError("");
                          setshowOtpError(false);
                        }}
                      />
                      {errors.verificationCode?.message && (
                        <FormInputError
                          errorMessage={errors.verificationCode.message}
                        />
                      )}
                      {showOtpError && (
                        <FormInputError errorMessage={otpError} />
                      )}
                    </div>
                    <Countdown
                      date={currentCounterTime}
                      key={currentCounterIndex}
                      renderer={renderer}
                    />
                  </div>
                </div>
                {/* <!-- Buttons --> */}
                <div className="block text-right mt-6">
                  <button
                    type="button"
                    className="ui-btn-secondary ui-btn-min-w mt-4"
                    onClick={() => setDialogState(false)}
                  >
                    <Trans
                      i18nKey="UserSecurityDialog-step1-form-confirmationCodeCancelButton"
                      components={{
                        span: <span className="sr-only" />,
                      }}
                    />
                  </button>
                  <button className="ui-btn-primary ui-btn-min-w mt-4">
                    <Trans
                      i18nKey="UserSecurityDialog-step1-form-confirmationCodeConfirmButton"
                      components={{
                        span: <span className="sr-only" />,
                      }}
                    />
                  </button>
                </div>
              </form>
            )}{" "}
            {step === 3 && (
              <>
                <div className="p-4 mx-auto md:p-6">
                  <h2 className="font-body font-bold text-xl">
                    {t(
                      "UserSecurityDialog-step3-form-securitySettingsUpdateHeading"
                    )}
                  </h2>
                  <p className="text-sm">
                    {t(
                      "UserSecurityDialog-step3-form-securitySettingsUpdateParagraph"
                    )}
                  </p>
                  <div className="text-xl text-center font-semibold mt-4 md:mt-6">
                    {t(
                      "UserSecurityDialog-step3-form-securitySettingsUpdate-thankYouText"
                    )}
                  </div>
                  <div className="text-center mt-4 md:mt-6">
                    <button
                      type="button"
                      className="ui-btn-secondary ui-btn-min-w mt-4"
                      onClick={() => window.location.reload(false)}
                    >
                      {" "}
                      {t(
                        "UserSecurityDialog-step3-form-securitySettingsUpdate-closeText"
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </EverlakeLoadingOverlay>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
